<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';

  const capabilities = [
    { label: 'Complete AI platform layer', description: 'Orchestration, security, governance, infrastructure' },
    { label: 'White-label ready', description: 'Your brand, your product, Corral invisible' },
    { label: 'Multi-tenant support', description: 'Root project isolation per customer for SaaS deployments' },
    { label: 'Per-customer deployment', description: 'For regulated buyers who need their own cloud' },
    { label: 'API-first extensibility', description: 'Build your domain layer on standard patterns' },
    { label: 'Cumulative Restrictions Framework', description: 'Safe agent behavior across your entire product' },
    { label: 'Managed foundation', description: 'We maintain the platform while you maintain the product' },
  ];
</script>

<PageMeta
  title="Product Companies — Build a product. Not a platform."
  description="White-labeled AI foundation for product companies. Orchestration, security, governance — invisible underneath your product. Multi-tenant SaaS or per-customer deployment."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <p class="font-mono text-[14px] text-[#ee6018] mb-4">Product Companies</p>
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Build a product. Not a platform.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          You're building an AI-powered product. The domain expertise, the business logic, the customer relationships — that's your IP. The orchestration layer, the security framework, the governance tooling, the deployment infrastructure? That's plumbing every platform company has to build. And it's not what differentiates your product.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The pitch -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          The foundation underneath. Invisible. Maintained.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            Corral is the foundation underneath. White-labeled, invisible, maintained. Your team focuses on what makes your product unique. We handle the complete stack so you can ship faster and maintain less.
          </p>
        </div>

        <div class="my-10 border-l-2 border-[#ee6018] pl-6 py-2">
          <p class="font-mono font-light text-[18px] text-white leading-relaxed">
            Your team builds the product. We maintain the platform. Your customers never know the difference.
          </p>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: Capabilities -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="mb-10">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">What product companies get</span>
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1]">
          Everything under the hood.
        </h2>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-0">
        {#each capabilities as cap, i}
          <div class="flex items-start gap-4 py-5 px-1 {i < capabilities.length - 1 ? 'border-b border-[#333335]' : ''} {i % 2 !== 0 ? 'lg:pl-8' : 'lg:pr-8'} {i % 2 !== 0 ? 'lg:border-l lg:border-l-[#333335]' : ''}">
            <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
            <div>
              <span class="font-sans text-[15px] text-white">{cap.label}</span>
              <span class="font-mono font-light text-[13px] text-[#878787] ml-2">— {cap.description}</span>
            </div>
          </div>
        {/each}
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §3: Deployment flexibility -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="text-center mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-[#020202] tracking-[-0.04em] leading-[1.1]">
          You choose. Per customer.
        </h2>
      </div>

      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#D3D3D3] z-[5]" aria-hidden="true"></div>
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" color="#D3D3D3" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-0">
          <div class="bg-white border border-[#D3D3D3] p-8 lg:p-10">
            <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-4 block">Option A</span>
            <h3 class="font-sans text-[24px] text-[#020202] tracking-[-0.03em] leading-[1.2] mb-4">
              Multi-tenant SaaS
            </h3>
            <p class="font-mono font-light text-[14px] text-[#5C5855] leading-relaxed">
              Deploy one instance with root project isolation per customer. Standard SaaS model — your infrastructure, your ops, your margins.
            </p>
          </div>
          <div class="bg-white border border-[#D3D3D3] p-8 lg:p-10 -mt-px lg:mt-0 lg:-ml-px">
            <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-4 block">Option B</span>
            <h3 class="font-sans text-[24px] text-[#020202] tracking-[-0.03em] leading-[1.2] mb-4">
              Per-customer tenant
            </h3>
            <p class="font-mono font-light text-[14px] text-[#5C5855] leading-relaxed">
              Deploy into each client's own cloud for regulated industries. Same Corral. Same codebase. Complete data isolation by architecture.
            </p>
          </div>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
